import { useUtmTags } from '@mm/hooks';
import { useHotjarMobileMonkey } from '@mm/state/hooks/use-hotjar';
import companyCRUDActions from '@mm/state/actions/CompanyCRUDActions';
import facebookSessionCRUDActions from '@mm/state/actions/FacebookSessionCRUDActions';
import { hideFacebookConnectModal } from '@mm/state/actions/networkActions';
import viewActions from '@mm/state/actions/viewActions';
import {
  activeChatbotIdSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import '@szhsin/react-menu/dist/index.css';
import classNames from 'classnames';
import { Location } from 'history';
import { map, values } from 'lodash';
import React, { Component, useEffect } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import 'react-dates/initialize';
import ReactGA from 'react-ga4';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { MandatoryUpdateBanner } from './common/components/MandatoryUpdateBanner/MandatoryUpdateBanner';
import PAGES_HEAD_CONTENT from './common/components/MetaData/constants';
import MetaData from './common/components/MetaData/MetaData';
import { ScheduleDemoTab } from './common/components/ScheduleDemoTab/ScheduleDemoTab';
import Spinner from './common/components/Spinner';
import { ToastComponent } from './common/components/ToastComponent/ToastComponent';
import { VariableChatWidget } from './common/components/VariableChatWidget/VariableChatWidget';
import WidgetErrorModal from './common/components/WidgetErrorModal/WidgetErrorModal';
import * as routes from './common/constants/routes';
import DebugUtils from './common/helpers/DebugUtils/DebugUtils';
import { MMdndContext, getMMdndEvents } from './common/helpers/MMdndContext';
import GuardedPrivateRoute from './common/helpers/routes/GuardedPrivateRoute';
import { shouldRedirectToOnboarding } from './common/helpers/routes/helpers';
import { RouterComponentProps } from './common/helpers/routes/interfaces';
import { PublicOnlyRoute } from './common/helpers/routes/PublicOnlyRoute';
import { Refresh } from './common/helpers/routes/Refresh';
import { UserRoleContainer } from './common/hooks/use-route-guard';
import { ModalRoot } from './common/modals/ModalRoot';
import OnboardingParentRoute from './dashboard/pages/SignupOnboarding/OnboardingParentRoute';
import SlideoutMenu from './editor/components/SlideoutMenu/SlideoutMenu';
import FacebookConnectModal from './editor/features/build/components/modals/FacebookConnectModal/FacebookConnectModal';
import GlobalHooksContainer from './GlobalHooksContainer';
import { AuthRouter } from './modules/LoginRouter/AuthRouter';
import { RouteType } from './routes/interfaces';
import { Routes } from './routes/top-level-routes';

type OuterProps = {};

type InnerProps = RouterComponentProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

function mapStateToProps(state: StoreState) {
  return {
    session: state.session.session,
    displaySpinner: values(state).some(val => val.busy === true),
    companyId: selectActiveCompanyId(state),
    view: state.view,
    fbModalConfig: state.network.facebookConnectModal,
    botId: activeChatbotIdSelector(state),
    botWidget: state.env.botWidget,
    modalIsOpen: state.modalState.isOpen,
    isTourOpen: state.modalState.isTourOpen || state.modalState.isOpen,
    gaTrackingId: state.env.gaTrackingId,
  };
}

function mapDispatchToProps(dispatch: Dispatch<StoreState>) {
  return {
    getCompanies: () => dispatch(companyCRUDActions.retrieveAll()),
    hideFbModal: () => dispatch(hideFacebookConnectModal()),
    createFBSession: ({ facebookToken }) =>
      dispatch(facebookSessionCRUDActions.create({ facebookToken })),
    appendToAppNavigationHistory: (location: Location) =>
      dispatch(viewActions.appendAppNavigationHistory(location)),
  };
}

class Main extends Component<Props, {}> {
  // tslint:disable-next-line: function-name
  UNSAFE_componentWillUpdate(nextProps: Props) {
    const { session, gaTrackingId } = this.props;

    if (!gaTrackingId && nextProps.gaTrackingId) {
      ReactGA.initialize(nextProps.gaTrackingId);
    }
    if (nextProps.gaTrackingId) {
      if (session) {
        ReactGA.set({ userId: session.id });
      }
    }
  }

  hideFbModal = (redirectTo: string) => () => {
    const {
      hideFbModal,
      history,
      fbModalConfig: { canBeHided },
    } = this.props;

    if (!canBeHided) return;

    hideFbModal();

    if (redirectTo) {
      return history.push(redirectTo);
    }
    history.goBack();
  };

  unlistenHistory = () => {};

  // tslint:disable-next-line: function-name
  UNSAFE_componentWillMount() {
    this.unlistenHistory = this.props.history.listen((location, action) => {
      this.props.appendToAppNavigationHistory(location);
    });
  }

  componentWillUnmount() {
    this.unlistenHistory();
  }

  showGlobalSpinner() {
    const { location, displaySpinner } = this.props;
    const onOnboardingPage =
      location.pathname.indexOf(Routes.signupOnboarding.getLink()) !== -1;

    return !onOnboardingPage && displaySpinner;
  }

  getRedirectLink() {
    const {
      session,
      location: { pathname },
    } = this.props;

    if (session && !session.isGuest) {
      const { needsToCreateCompany } = session;
      const { onboardingSignupStep } = session.userAttributes;

      if (needsToCreateCompany) {
        return routes.SIGNUP;
      }

      if (
        session &&
        shouldRedirectToOnboarding(pathname, onboardingSignupStep, false)
      ) {
        return Routes.signupOnboarding.getLink();
      }

      return routes.CHATBOT_EDITOR;
    }

    return routes.LOGIN;
  }

  onConnect = async ({ facebookToken }) => {
    const { createFBSession, hideFbModal } = this.props;
    await createFBSession({ facebookToken });
    hideFbModal();
    location.reload();
  };

  render() {
    const { show, message, permissions } = this.props.fbModalConfig;

    const dndEvents = getMMdndEvents();

    const mainClassName = classNames('main-area', {
      'main-area--tour-open': this.props.isTourOpen,
    });

    return (
      <UserRoleContainer.Provider>
        <div className={mainClassName}>
          <SlideoutMenu />

          <MMdndContext.Provider value={dndEvents}>
            <DragDropContext
              onDragStart={dndEvents.onDragStart}
              onDragUpdate={dndEvents.onDragUpdate}
              onDragEnd={dndEvents.onDragEnd}
            >
              <GlobalHooksContainer />
              {!IS_PROD && <DebugUtils />}
              <MandatoryUpdateBanner />
              <MetaData
                title={PAGES_HEAD_CONTENT.MAIN.title}
                description={PAGES_HEAD_CONTENT.MAIN.meta.description}
              />
              <ScheduleDemoTab />
              <Spinner
                show={this.showGlobalSpinner()}
                className="main-loader"
              />
              <FacebookConnectModal
                show={show}
                hide={this.hideFbModal}
                message={message}
                permissions={permissions}
                onConnect={this.onConnect}
              />
              <WidgetErrorModal />
              <ToastComponent />
              <Switch>
                {AuthRouter}
                <Refresh path={routes.REFRESH} />
                {map(Routes, (value, id) => {
                  const { path, routeType, component } = value;
                  switch (routeType) {
                    case RouteType.PublicOnly:
                      return (
                        <PublicOnlyRoute
                          key={id}
                          path={path}
                          component={component}
                          redirectProps={{
                            to: routes.HOME,
                          }}
                        />
                      );
                    case RouteType.PrivateOnly:
                      return (
                        <GuardedPrivateRoute
                          key={id}
                          path={path}
                          component={component}
                        />
                      );
                    case RouteType.PublicPrivate:
                      return (
                        <Route key={id} path={path} component={component} />
                      );
                    case RouteType.OnboardingParentRoute:
                      return (
                        <OnboardingParentRoute
                          key={id}
                          path={path}
                          component={component}
                        />
                      );
                  }
                })}
                <Redirect to={this.getRedirectLink()} />
              </Switch>
              <ModalRoot />
              <VariableChatWidget />
            </DragDropContext>
          </MMdndContext.Provider>
        </div>
      </UserRoleContainer.Provider>
    );
  }
}

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

const StatedMain = enhance(Main);

export default () => {
  useUtmTags();
  useHotjarMobileMonkey();

  return <StatedMain />;
};
